import { Box, Button, Flex, Grid, GridItem, Image, useColorMode } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { assets } from "../../../assets/assets";
import { OrgWithOwnerDto, bffApi, useCreateOrganizationForMeMutation } from "../../../autogen/bff-api";
import { validateReturnTo } from "../../../common/auth/returnTo";
import { useApiError } from "../../../common/errors/useApiError";
import { useAppDispatch } from "../../../common/redux/hooks";
import { SignupError } from "../../../common/redux/thunks/auth/signup-as-person-thunk";
import { useBaseData } from "../../../common/useBaseData";
import { authService, requireStringEnvVar } from "../../../config";
import { DuplicateOrgsModal } from "../shared/DuplicateOrgsModal";
import { RegisterOrgOwnerForm, RegisterOrgOwnerFormValues } from "./RegisterOrgOwnerForm";
import { RegisterOrganizationForm, RegisterOrganizationFormValues } from "./RegisterOrganizationForm";

type Step = "orgInfo" | "owner";

export const Register = () => {
  const { t } = useTranslation();
  const theme = useColorMode();
  const dispatch = useAppDispatch();
  const baseData = useBaseData();
  const apiError = useApiError();
  const [createOrg] = useCreateOrganizationForMeMutation();
  const [searchParams] = useSearchParams();
  const returnTo = validateReturnTo(searchParams.get("return_to"));

  const [step, setStep] = useState<Step>("orgInfo");
  const [orgInfo, setOrgInfo] = useState<RegisterOrganizationFormValues | null>(null);
  const [duplicateOrgs, setDuplicateOrgs] = useState<OrgWithOwnerDto[] | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupError, setSignupError] = useState<SignupError | "UnknownError" | null>(null);

  const onOrgInfoContinue = async (values: RegisterOrganizationFormValues) => {
    setIsSubmitting(true);
    try {
      const trimmedOrgnr = values.organizationNumber?.replace(/\s+/g, "") ?? "";
      const normalizedValues: RegisterOrganizationFormValues = { ...values, organizationNumber: trimmedOrgnr };
      const country = baseData.countries.find((c) => c.id === values.countryId);

      setOrgInfo(normalizedValues);

      if (country && trimmedOrgnr) {
        try {
          const { orgs } = await dispatch(
            bffApi.endpoints.listOrgsByNumber.initiate({ country: country.alpha3Code, number: trimmedOrgnr })
          ).unwrap();
          if (orgs.length > 0) {
            setDuplicateOrgs(orgs);
            return;
          }
        } catch (e) {
          apiError.trigger(e);
          return;
        }
      }
      setStep("owner");
    } finally {
      setIsSubmitting(false);
    }
  };

  const continueWithSeparateOrg = () => {
    setDuplicateOrgs(null);
    setStep("owner");
  };

  const onIdentitySubmit = async (identity: RegisterOrgOwnerFormValues) => {
    if (!orgInfo) return;
    setIsSubmitting(true);
    setSignupError(null);
    try {
      const signupResult = await authService.signup({
        email: identity.email,
        password: identity.password,
        firstName: identity.firstName,
        lastName: identity.lastName,
        roles: [],
      });
      if (signupResult !== "Success") {
        setSignupError(signupResult === "Failure" ? "UnknownError" : signupResult);
        return;
      }
      try {
        await createOrg({
          createOrgRequest: {
            companyName: orgInfo.companyName,
            countryId: orgInfo.countryId,
            organizationNumber: orgInfo.organizationNumber || undefined,
            emailInviteContext: undefined,
          },
        }).unwrap();
        const baseUrl = requireStringEnvVar("VITE_WEBAPP_BASE_URL");
        // Trim a trailing slash on baseUrl before concatenation; returnTo always starts with "/".
        window.location.replace(returnTo ? baseUrl.replace(/\/$/, "") + returnTo : baseUrl);
      } catch (e) {
        apiError.trigger(e);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const goBack = () => {
    setStep("orgInfo");
    setSignupError(null);
  };

  const stepContent: Record<Step, React.ReactNode> = {
    orgInfo: (
      <RegisterOrganizationForm
        isSubmitting={isSubmitting}
        defaultValues={orgInfo ?? undefined}
        onContinue={onOrgInfoContinue}
      />
    ),
    owner: (
      <>
        <Flex mb={4}>
          <Button variant="link" onClick={goBack} colorScheme="teal" size="sm">
            ← {t("Back to organization info")}
          </Button>
        </Flex>
        <RegisterOrgOwnerForm isSubmitting={isSubmitting} signupError={signupError} onSubmit={onIdentitySubmit} />
      </>
    ),
  };

  return (
    <Box
      width="full"
      minHeight="100vh"
      bgGradient="linear(to-br, smTealShadow, smBackground, smBackground, smBackground, smBlueShadow)"
    >
      <Grid
        as="main"
        gridTemplateColumns={{ base: "16px minmax(0, 1fr) 16px", sm: "1fr 450px 1fr" }}
        minHeight="100vh"
        gridTemplateRows={"1fr auto 2fr"}
        templateAreas={`
                "left top right"
                "left register right"
                "left bottom right"
                `}
      >
        <GridItem area={"left"}></GridItem>
        <GridItem area={"top"}></GridItem>
        <GridItem area={"bottom"}></GridItem>
        <GridItem area={"right"}></GridItem>
        <GridItem
          area={"register"}
          borderRadius={10}
          bg="smBackground"
          boxShadow="smAuthCard"
          padding={{ base: 6, sm: 10 }}
          my={10}
        >
          <Flex w="full" h="full" flexDirection="column">
            <Flex pb="10">
              <Image src={theme.colorMode === "dark" ? assets.logo_dark : assets.logo} alt="SourceMagnet" h="30px" mt="10px" />
            </Flex>
            {stepContent[step]}
          </Flex>
        </GridItem>
      </Grid>

      <DuplicateOrgsModal
        duplicateOrgs={duplicateOrgs}
        onCancel={() => setDuplicateOrgs(null)}
        onContinue={continueWithSeparateOrg}
      />
    </Box>
  );
};
